import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
    name: "",
    email: "",
    password: "",
    role: "member",
    phone: "",
    designation: "",
};

/**
 * Reusable add/edit user dialog (admin-only on the backend). Used by the Users
 * page and inline from the New Task form. `userToEdit` (object) = edit mode;
 * `initialName` pre-fills the name. `onSaved(savedUser)` fires with the API response.
 */
export default function UserFormDialog({
    open,
    onOpenChange,
    userToEdit = null,
    initialName = "",
    onSaved,
}) {
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(
            userToEdit
                ? {
                      name: userToEdit.name || "",
                      email: userToEdit.email,
                      password: "",
                      role: userToEdit.role,
                      phone: userToEdit.phone || "",
                      designation: userToEdit.designation || "",
                  }
                : { ...empty, name: initialName || "" },
        );
    }, [open, userToEdit, initialName]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        if (!userToEdit && form.password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setSubmitting(true);
        try {
            let saved;
            if (userToEdit) {
                const payload = {
                    name: form.name,
                    role: form.role,
                    phone: form.phone || null,
                    designation: form.designation || null,
                };
                if (form.password) payload.password = form.password;
                ({ data: saved } = await api.patch(`/users/${userToEdit.id}`, payload));
                toast.success("User updated");
            } else {
                ({ data: saved } = await api.post("/users", {
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    role: form.role,
                    phone: form.phone || null,
                    designation: form.designation || null,
                }));
                toast.success("User created");
            }
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                        {userToEdit ? "Edit user" : "Add user"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label>Full name <span className="text-primary">*</span></Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="user-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email <span className="text-primary">*</span></Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                                disabled={!!userToEdit}
                                data-testid="user-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) => setForm({ ...form, role: v })}
                            >
                                <SelectTrigger data-testid="user-role-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="member">Member</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Designation</Label>
                            <Input
                                value={form.designation}
                                onChange={(e) =>
                                    setForm({ ...form, designation: e.target.value })
                                }
                                placeholder="Senior, Article, Partner..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>
                                {userToEdit ? "Reset password (optional)" : "Initial password"}{" "}
                                {!userToEdit && <span className="text-primary">*</span>}
                            </Label>
                            <Input
                                type="password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                placeholder={userToEdit ? "Leave blank to keep current" : ""}
                                data-testid="user-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="user-save-button">
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : userToEdit ? (
                                "Save changes"
                            ) : (
                                "Create user"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
